/** Inline SVG icons for the viewer toolbar — no external dependency needed */

interface IconProps {
  size?: number;
  color?: string;
}

const D = ({ size = 16, color = 'currentColor' }: IconProps) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export function IconWL(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18" />
      <path d="M3 12h9" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
  );
}

export function IconPan(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M5 9l-3 3 3 3" />
      <path d="M19 9l3 3-3 3" />
      <path d="M9 5l3-3 3 3" />
      <path d="M9 19l3 3 3-3" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
    </svg>
  );
}

export function IconZoom(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
      <line x1="8" y1="11" x2="14" y2="11" />
      <line x1="11" y1="8" x2="11" y2="14" />
    </svg>
  );
}

export function IconMagnify(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
      <circle cx="11" cy="11" r="3" />
    </svg>
  );
}

export function IconScroll(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <line x1="12" y1="7" x2="12" y2="11" />
      <path d="M10 9l2-2 2 2" />
    </svg>
  );
}

export function IconCine(p: IconProps) {
  return (
    <svg {...D(p)}>
      <polygon points="6,4 20,12 6,20" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
  );
}

export function IconProbe(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="12" cy="12" r="2" fill={p.color || 'currentColor'} stroke="none" />
      <line x1="12" y1="2" x2="12" y2="8" />
      <line x1="12" y1="16" x2="12" y2="22" />
      <line x1="2" y1="12" x2="8" y2="12" />
      <line x1="16" y1="12" x2="22" y2="12" />
    </svg>
  );
}

export function IconLength(p: IconProps) {
  return (
    <svg {...D(p)}>
      <line x1="4" y1="20" x2="20" y2="4" />
      <line x1="4" y1="16" x2="4" y2="20" />
      <line x1="4" y1="20" x2="8" y2="20" />
      <line x1="20" y1="4" x2="16" y2="4" />
      <line x1="20" y1="4" x2="20" y2="8" />
    </svg>
  );
}

export function IconAngle(p: IconProps) {
  return (
    <svg {...D(p)}>
      <polyline points="4,20 12,6 20,20" />
      <path d="M8 14.5 L16 14.5" strokeDasharray="2 2" />
    </svg>
  );
}

/** Cobb angle: two non-touching line segments (end-plates of a scoliosis
 *  measurement) with a dashed arc hinting at the angle measured between
 *  their virtual extensions. Visually distinct from IconAngle (which has
 *  the two lines sharing a vertex). */
export function IconCobb(p: IconProps) {
  return (
    <svg {...D(p)}>
      {/* upper end-plate tilted left-down */}
      <line x1="3" y1="6" x2="14" y2="9" />
      {/* lower end-plate tilted right-down */}
      <line x1="10" y1="19" x2="21" y2="16" />
      {/* dashed arc between the two line midpoints */}
      <path d="M9 12 A 6 6 0 0 1 15 13" strokeDasharray="2 2" fill="none" />
    </svg>
  );
}

export function IconRect(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="18" height="18" rx="1" strokeDasharray="4 2" />
    </svg>
  );
}

export function IconEllipse(p: IconProps) {
  return (
    <svg {...D(p)}>
      <ellipse cx="12" cy="12" rx="9" ry="6" strokeDasharray="4 2" />
    </svg>
  );
}

export function IconArrow(p: IconProps) {
  return (
    <svg {...D(p)}>
      <line x1="5" y1="19" x2="19" y2="5" />
      <polyline points="12,5 19,5 19,12" />
    </svg>
  );
}

export function IconCrosshairs(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="12" cy="12" r="8" />
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
    </svg>
  );
}

export function IconInvert(p: IconProps) {
  return (
    <svg {...D(p)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 1 0 18z" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
  );
}

export function IconFlipH(p: IconProps) {
  return (
    <svg {...D(p)}>
      <line x1="12" y1="3" x2="12" y2="21" strokeDasharray="2 2" />
      <polyline points="6,8 2,12 6,16" />
      <polyline points="18,8 22,12 18,16" />
    </svg>
  );
}

export function IconFlipV(p: IconProps) {
  return (
    <svg {...D(p)}>
      <line x1="3" y1="12" x2="21" y2="12" strokeDasharray="2 2" />
      <polyline points="8,6 12,2 16,6" />
      <polyline points="8,18 12,22 16,18" />
    </svg>
  );
}

export function IconRotateL(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M2 12a10 10 0 0 1 17.3-6.8" />
      <polyline points="2,4 2,12 10,12" />
    </svg>
  );
}

export function IconRotateR(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M22 12a10 10 0 0 0-17.3-6.8" />
      <polyline points="22,4 22,12 14,12" />
    </svg>
  );
}

export function IconReset(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <polyline points="3,2 3,8 9,8" />
    </svg>
  );
}

export function IconClear(p: IconProps) {
  return (
    <svg {...D(p)}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconOverlay(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="7" y1="7" x2="11" y2="7" />
      <line x1="7" y1="10" x2="9" y2="10" />
      <line x1="15" y1="17" x2="17" y2="17" />
    </svg>
  );
}

export function IconLayout(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="3" y1="12" x2="21" y2="12" />
    </svg>
  );
}

export function IconMPR(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="2" y="8" width="10" height="10" rx="1" />
      <rect x="8" y="2" width="10" height="10" rx="1" />
      <path d="M14 14l6 6" />
    </svg>
  );
}

export function Icon3D(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

export function IconMIP(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 17c2-4 4-10 5-10s3 6 5 10" />
    </svg>
  );
}

export function IconBack(p: IconProps) {
  return (
    <svg {...D(p)}>
      <polyline points="15,18 9,12 15,6" />
    </svg>
  );
}

export function IconSync(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M4 12a8 8 0 0 1 14.3-4.8" />
      <polyline points="20,2 20,8 14,8" />
      <path d="M20 12a8 8 0 0 1-14.3 4.8" />
      <polyline points="4,22 4,16 10,16" />
    </svg>
  );
}

export function IconZoomFit(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <polyline points="8,3 3,3 3,8" />
      <polyline points="16,3 21,3 21,8" />
      <polyline points="8,21 3,21 3,16" />
      <polyline points="16,21 21,21 21,16" />
    </svg>
  );
}

export function IconZoomActual(p: IconProps) {
  return (
    <svg {...D(p)}>
      <text x="6" y="17" fontSize="14" fontWeight="700" fill={p.color || 'currentColor'} stroke="none" fontFamily="sans-serif">1:1</text>
    </svg>
  );
}

export function IconDownload(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7,10 12,15 17,10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export function IconSave(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17,21 17,13 7,13 7,21" />
      <polyline points="7,3 7,8 15,8" />
    </svg>
  );
}

export function IconStar(p: IconProps) {
  return (
    <svg {...D(p)}>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

export function IconPriors(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="2" y="3" width="8" height="18" rx="1" />
      <rect x="14" y="3" width="8" height="18" rx="1" />
      <path d="M10 12h4" strokeDasharray="2 1" />
    </svg>
  );
}

export function IconReport(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </svg>
  );
}

export function IconProtocols(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <circle cx="17.5" cy="6.5" r="1" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
  );
}

export function IconCompare(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="2" y="4" width="9" height="16" rx="1" />
      <rect x="13" y="4" width="9" height="16" rx="1" />
      <path d="M11 12h2" />
    </svg>
  );
}

export function IconMonitor(p: IconProps) {
  return (
    <svg {...D(p)}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

/** Basket / cart — used for the "Add to Composer" toolbar affordance. */
export function IconBasketAdd(p: IconProps) {
  return (
    <svg {...D(p)}>
      <path d="M3 6h18l-2 12H5L3 6z" />
      <path d="M8 6l4-4 4 4" />
      <line x1="12" y1="10" x2="12" y2="16" />
      <line x1="9" y1="13" x2="15" y2="13" />
    </svg>
  );
}

/** Printer / send — used for the "Send basket to Composer" toolbar button. */
export function IconPrint(p: IconProps) {
  return (
    <svg {...D(p)}>
      <polyline points="6,9 6,2 18,2 18,9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}
